"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import {
  Button,
  ButtonVariant,
  Text,
  TextColor,
  TextSize,
  TextWeight,
} from "../../components";
import { useMatchBreakpoints } from "../../hooks";
import type { CategoryFlat } from "../../types/category.type";
import { Modal } from "../Modal";

interface CategoriesTreeProps {
  categoriesData?: CategoryFlat[];
  activeCategory?: string;
  expandedCategories: { [key: string]: boolean };
  expandedSubcategories: { [key: string]: boolean };
  expandedSubSubcategories: { [key: string]: boolean };
  onToggleCategory: (categorySlug: string) => void;
  onToggleSubcategory: (subcategorySlug: string) => void;
  onToggleSubSubcategory: (subSubcategorySlug: string) => void;
  onSubcategoryClick: (subcategorySlug: string) => void;
  onSetExpandedCategories?: (expanded: { [key: string]: boolean }) => void;
  onSetExpandedSubcategories?: (expanded: { [key: string]: boolean }) => void;
  onSetExpandedSubSubcategories?: (expanded: {
    [key: string]: boolean;
  }) => void;
}

const CategoryIcon = styled.span<{ isExpanded: boolean }>`
  transition: transform 0.2s ease;
`;

const SubcategoryList = styled.div<{ isExpanded: boolean }>`
  overflow: hidden;
  transition: max-height 0.3s ease;
  max-height: ${(props) => (props.isExpanded ? "auto" : "0px")};
`;

const CategoriesTree: React.FC<CategoriesTreeProps> = ({
  categoriesData = [],
  activeCategory,
  expandedCategories = {},
  expandedSubcategories = {},
  expandedSubSubcategories = {},
  onToggleCategory,
  onToggleSubcategory,
  onToggleSubSubcategory,
  onSubcategoryClick,
  onSetExpandedCategories,
  onSetExpandedSubcategories,
  onSetExpandedSubSubcategories,
}) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const pathname = usePathname();

  const { isXs, isSm, isMd, isLg, isXl } = useMatchBreakpoints();
  const isMobile = isXs || isSm || isMd || isLg || isXl;

  // Funkcja do znajdowania i rozwijania kategorii na podstawie pathname
  const expandCategoriesFromPath = (path: string) => {
    if (!path || !categoriesData.length) return;

    // Usuń pierwszy slash i podziel ścieżkę na segmenty
    const segments = path.replace(/^\//, "").split("/");

    if (segments.length === 0) return;

    const [mainCategorySlug, subcategorySlug, subSubcategorySlug] = segments;

    // Znajdź główną kategorię
    const mainCategory = categoriesData.find(
      (cat) => cat.slug === mainCategorySlug,
    );

    if (!mainCategory) return;

    // ZAMKNIJ WSZYSTKIE KATEGORIE I PODKATEGORIE
    const newExpandedCategories: { [key: string]: boolean } = {};
    const newExpandedSubcategories: { [key: string]: boolean } = {};
    const newExpandedSubSubcategories: { [key: string]: boolean } = {};

    // Zawsze rozwiń główną kategorię
    newExpandedCategories[mainCategorySlug ?? ""] = true;

    // Jeśli mamy podkategorię w URL
    if (subcategorySlug && mainCategory.subcategories) {
      const subcategory = mainCategory.subcategories.find(
        (sub) => sub.slug === subcategorySlug,
      );

      if (subcategory) {
        // Jeśli podkategoria ma sub-subkategorie, rozwiń ją
        if (subcategory.subcategories && subcategory.subcategories.length > 0) {
          newExpandedSubcategories[subcategorySlug] = true;
        }

        // Jeśli mamy sub-subkategorię w URL
        if (subSubcategorySlug && subcategory.subcategories) {
          const subSubcategory = subcategory.subcategories.find(
            (subsub) => subsub.slug === subSubcategorySlug,
          );

          if (subSubcategory) {
            // Ustaw sub-subkategorię jako aktywną
            onSubcategoryClick(subSubcategorySlug);

            // Jeśli sub-subkategoria ma dalsze podkategorie, rozwiń ją też
            if (
              subSubcategory.subcategories &&
              subSubcategory.subcategories.length > 0
            ) {
              newExpandedSubSubcategories[subSubcategorySlug] = true;
            }
          }
        } else {
          // Jeśli nie ma sub-subkategorii w URL, ustaw podkategorię jako aktywną
          onSubcategoryClick(subcategorySlug);
        }
      }
    }

    // Ustaw wszystkie stany rozwinięcia
    if (onSetExpandedCategories) {
      onSetExpandedCategories(newExpandedCategories);
    }

    if (onSetExpandedSubcategories) {
      onSetExpandedSubcategories(newExpandedSubcategories);
    }

    if (onSetExpandedSubSubcategories) {
      onSetExpandedSubSubcategories(newExpandedSubSubcategories);
    }

    // Fallback jeśli nie ma setter funkcji
    if (!onSetExpandedCategories) {
      Object.keys(expandedCategories || {}).forEach((categorySlug) => {
        if (
          categorySlug !== mainCategorySlug &&
          expandedCategories[categorySlug]
        ) {
          onToggleCategory(categorySlug);
        }
      });
      if (!expandedCategories[mainCategorySlug ?? ""]) {
        onToggleCategory(mainCategorySlug ?? "");
      }
    }

    if (
      !onSetExpandedSubcategories &&
      subcategorySlug &&
      mainCategory.subcategories
    ) {
      const subcategory = mainCategory.subcategories.find(
        (sub) => sub.slug === subcategorySlug,
      );
      if (
        subcategory?.subcategories?.length &&
        !expandedSubcategories[subcategorySlug]
      ) {
        onToggleSubcategory(subcategorySlug);
      }
    }

    if (
      !onSetExpandedSubSubcategories &&
      subSubcategorySlug &&
      subcategorySlug &&
      mainCategory.subcategories
    ) {
      const subcategory = mainCategory.subcategories.find(
        (sub) => sub.slug === subcategorySlug,
      );
      if (subcategory?.subcategories) {
        const subSubcategory = subcategory.subcategories.find(
          (subsub) => subsub.slug === subSubcategorySlug,
        );
        if (
          subSubcategory?.subcategories?.length &&
          !expandedSubSubcategories[subSubcategorySlug]
        ) {
          onToggleSubSubcategory(subSubcategorySlug);
        }
      }
    }
  };

  useEffect(() => {
    console.log("Current pathname:", pathname);
    console.log("Categories data:", categoriesData.length);
    expandCategoriesFromPath(pathname);
  }, [pathname, categoriesData]);

  const handleSubcategoryClick = (
    subcategorySlug: string,
    hasSubcategories: boolean,
  ): void => {
    if (hasSubcategories) {
      onToggleSubcategory(subcategorySlug);
    } else {
      onSubcategoryClick(subcategorySlug);
    }
  };

  const handleSubSubcategoryClick = (
    subSubcategorySlug: string,
    hasSubcategories: boolean,
  ): void => {
    if (hasSubcategories) {
      onToggleSubSubcategory(subSubcategorySlug);
    } else {
      onSubcategoryClick(subSubcategorySlug);
    }
  };

  const renderSubSubSubcategory = (
    parentSlug: string,
    subcategorySlug: string,
    subSubSubcategory: CategoryFlat,
  ): JSX.Element => {
    const isActive: boolean = activeCategory === subSubSubcategory.slug;

    return (
      <div
        key={subSubSubcategory.idCategory}
        className={`flex justify-between items-center py-1.5 cursor-pointer text-xs text-gray-500 hover:text-gray-700 transition-colors ml-8 ${
          isActive
            ? "border-l-2 border-gray-700 pl-3 text-gray-700 font-medium"
            : "border-l-2 border-transparent pl-3"
        }`}
        onClick={() => onSubcategoryClick(subSubSubcategory.slug)}
      >
        <Link
          href={`/${parentSlug}/${subcategorySlug}/${subSubSubcategory.slug}`}
          onClick={() => onSubcategoryClick(subSubSubcategory.slug)}
        >
          <div className="flex items-center gap-2">
            <Text
              text={subSubSubcategory.name}
              color={TextColor.GRAYSCALE_700}
              fontSize={TextSize.XXS}
            />

            <Text
              text={`(${subSubSubcategory.activeProductsCount})`}
              color={TextColor.GRAYSCALE_400}
              fontSize={TextSize.XXS}
            />
          </div>
        </Link>
      </div>
    );
  };

  const renderSubSubcategory = (
    parentSlug: string,
    subSubcategory: CategoryFlat,
  ): JSX.Element => {
    const isActive: boolean = activeCategory === subSubcategory.slug;
    const hasSubcategories: boolean =
      subSubcategory.subcategories && subSubcategory.subcategories.length > 0;
    const isExpanded: boolean =
      (expandedSubSubcategories &&
        expandedSubSubcategories[subSubcategory.slug ?? ""]) ||
      false;

    return (
      <div key={`${parentSlug}-${subSubcategory.idCategory}`}>
        <div
          className={`flex justify-between items-center py-1.5 cursor-pointer text-xs text-gray-500 hover:text-gray-700 transition-colors ml-4 ${
            isActive
              ? "border-l-2 border-gray-700 pl-3 text-gray-700 font-medium"
              : "border-l-2 border-transparent pl-3"
          }`}
        >
          <Link
            href={`/${parentSlug}/${subSubcategory.slug}`}
            onClick={() => onSubcategoryClick(subSubcategory.slug)}
          >
            <div className="flex items-center gap-2">
              <Text
                text={subSubcategory.name}
                color={TextColor.GRAYSCALE_700}
                fontSize={TextSize.XS}
              />

              <Text
                text={`(${subSubcategory.activeProductsCount})`}
                color={TextColor.GRAYSCALE_400}
                fontSize={TextSize.XXS}
              />
            </div>
          </Link>
          <div
            className="flex items-center justify-center gap-2 w-6"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSubSubcategoryClick(subSubcategory.slug, hasSubcategories);
            }}
          >
            {hasSubcategories && (
              <CategoryIcon className="text-xs" isExpanded={isExpanded}>
                {isExpanded ? "−" : "+"}
              </CategoryIcon>
            )}
          </div>
        </div>

        {/* Renderowanie sub-sub-subkategorii */}
        {hasSubcategories && (
          <SubcategoryList isExpanded={isExpanded}>
            <div className="pb-4">
              {subSubcategory.subcategories?.map((subSubSubcategory) =>
                renderSubSubSubcategory(
                  parentSlug,
                  subSubcategory.slug,
                  subSubSubcategory,
                ),
              )}
            </div>
          </SubcategoryList>
        )}
      </div>
    );
  };

  const renderSubcategory = (
    parentSlug: string,
    subcategory: CategoryFlat,
  ): JSX.Element => {
    const isActive: boolean = activeCategory === subcategory.slug;
    const hasSubcategories: boolean =
      subcategory.subcategories && subcategory.subcategories.length > 0;
    const isExpanded: boolean =
      (expandedSubcategories &&
        expandedSubcategories[subcategory.slug ?? ""]) ||
      false;

    return (
      <div key={`${parentSlug}-${subcategory.idCategory}`}>
        <div
          className={`flex justify-between items-center py-1.5 cursor-pointer text-sm text-gray-600 hover:text-gray-900 transition-colors ${
            isActive
              ? "border-l-2 border-gray-900 pl-4 text-gray-900"
              : "border-l-2 border-transparent pl-4"
          }`}
        >
          <Link
            href={`/${parentSlug}/${subcategory.slug}`}
            onClick={() => onSubcategoryClick(subcategory.slug)}
          >
            <div className="flex items-center gap-2">
              <Text
                text={subcategory.name}
                color={TextColor.GRAYSCALE_700}
                fontSize={TextSize.S}
              />

              <Text
                text={`(${subcategory.activeProductsCount})`}
                color={TextColor.GRAYSCALE_400}
                fontSize={TextSize.XXS}
              />
            </div>
          </Link>
          <div
            className="flex items-center justify-center gap-2 w-6"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSubcategoryClick(subcategory.slug, hasSubcategories);
            }}
          >
            {hasSubcategories && (
              <CategoryIcon className="text-xs" isExpanded={isExpanded}>
                {isExpanded ? "−" : "+"}
              </CategoryIcon>
            )}
          </div>
        </div>

        {/* Renderowanie sub-subkategorii */}
        {hasSubcategories && (
          <SubcategoryList isExpanded={isExpanded}>
            <div className="pb-4">
              {subcategory.subcategories?.map((subSubcategory) =>
                renderSubSubcategory(parentSlug, subSubcategory),
              )}
            </div>
          </SubcategoryList>
        )}
      </div>
    );
  };

  const renderCategory = (category: CategoryFlat): JSX.Element => {
    const isExpanded: boolean =
      (expandedCategories && expandedCategories[category.slug ?? ""]) || false;

    return (
      <div key={category.idCategory}>
        <div className="flex justify-between items-center pb-2 cursor-pointer hover:text-gray-600 transition-colors">
          <Link href={`/${category.slug}`}>
            <div className="flex items-center gap-2">
              <Text
                text={category.name}
                weight={TextWeight.SEMIBOLD}
                className="tracking-wider"
                textTransform="uppercase"
                color={TextColor.GRAYSCALE_900}
                fontSize={TextSize.S}
              />
              <Text
                text={`(${category.activeProductsCount})`}
                color={TextColor.GRAYSCALE_400}
                fontSize={TextSize.XXS}
              />
            </div>
          </Link>
          <div
            className="flex items-center justify-center gap-2 w-6"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleCategory(category.slug);
            }}
          >
            <CategoryIcon className="text-xl" isExpanded={isExpanded}>
              {isExpanded ? "−" : "+"}
            </CategoryIcon>
          </div>
        </div>

        <SubcategoryList isExpanded={isExpanded}>
          <div className="pb-4">
            {category.subcategories?.map((subcategory) =>
              renderSubcategory(category.slug, subcategory),
            )}
          </div>
        </SubcategoryList>
      </div>
    );
  };

  return isMobile ? (
    <div>
      <Button
        text="Kategorie"
        onClick={() => setIsModalOpen(true)}
        variant={ButtonVariant.SECONDARY}
        className="!w-full"
      />

      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        fullSpaceModal
      >
        <div className="px-8 pt-16 mb-8">
          {categoriesData.map(renderCategory)}
        </div>
      </Modal>
    </div>
  ) : (
    <div className="font-sans text-gray-900 min-w-64 max-w-sm">
      {categoriesData.map(renderCategory)}
    </div>
  );
};

export default CategoriesTree;
